<template>
  <div class="pt-3 border-t border-gray-100 mb-4">
    <div class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">AI Recommendation</div>
    <div class="flex items-center gap-2 flex-wrap">
      <span :class="['px-2 py-0.5 text-xs font-semibold rounded', dispositionClass]">{{ dispositionLabel }}</span>
      <span v-if="confidenceLabel" class="text-xs text-gray-500">{{ confidenceLabel }} confidence</span>
      <span v-if="runDateLabel" class="text-xs text-gray-400">Run {{ runDateLabel }}</span>
    </div>

    <div v-if="skilledCareNeeds.length" class="mt-2 flex items-center gap-1.5 flex-wrap">
      <span class="text-xs text-gray-500">Skilled care</span>
      <span
        v-for="need in skilledCareNeeds"
        :key="need"
        class="px-1.5 py-px text-xs rounded bg-gray-100 text-gray-700"
      >{{ need }}</span>
    </div>

    <div v-if="pillars.length" class="mt-2 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
      <template v-for="pillar in pillars" :key="pillar.key">
        <span class="text-xs text-gray-500">{{ pillar.label }}</span>
        <span :class="['text-xs font-medium', pillar.className]">{{ pillar.value }}</span>
      </template>
    </div>

    <div v-if="reason" class="mt-2">
      <div class="text-xs text-gray-500 mb-0.5">Rationale</div>
      <p class="text-xs text-gray-800 whitespace-pre-wrap">{{ reason }}</p>
    </div>

    <div v-if="missingData.length" class="mt-2">
      <div class="text-xs text-gray-500 mb-0.5">Data gaps to verify</div>
      <ul class="space-y-1">
        <li v-for="(item, idx) in missingData" :key="idx" class="text-xs text-gray-700 list-disc ml-4">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>

const DISPOSITIONS = {
  APPROVE: { label: 'Continue home health', className: 'bg-green-100 text-green-800' },
  DISCHARGE: { label: 'Recommend discharge', className: 'bg-red-100 text-red-700' },
  NEEDS_REVIEW: { label: 'Needs review', className: 'bg-amber-100 text-amber-800' },
};

const PILLARS = [
  { key: 'homebound_status', label: 'Homebound' },
  { key: 'skilled_need_status', label: 'Skilled need' },
  { key: 'recertification_validity', label: 'Recertification' },
  { key: 'physician_plan_of_care_status', label: 'Plan of care' },
];

const PILLAR_CLASSES = {
  MET: 'text-green-700',
  CURRENT: 'text-green-700',
  NOT_MET: 'text-red-600',
  LAPSED: 'text-red-600',
  INSUFFICIENT_DATA: 'text-amber-700',
};

const SKILLED_CARE_LABELS = {
  nursing: 'Nursing',
  PT: 'PT',
  OT: 'OT',
  SLP: 'SLP',
};

export default {
  name: 'Patient_Task_AIRecommendation',
  props: {
    metadata: {
      type: Object,
      required: true,
    },
    patientIdentifier: {
      type: [String, Number],
      default: null,
    },
  },
  computed: {
    disposition() {
      return DISPOSITIONS[this.metadata.ai_final_disposition] || null;
    },
    dispositionLabel() {
      return this.disposition?.label || this.metadata.ai_final_disposition || 'No recommendation';
    },
    dispositionClass() {
      return this.disposition?.className || 'bg-gray-100 text-gray-700';
    },
    confidenceLabel() {
      const value = Number(this.metadata.ai_confidence);
      if (!Number.isFinite(value)) return '';
      return `${Math.round(value * 100)}%`;
    },
    runDateLabel() {
      const raw = this.metadata.ai_run_date;
      if (!raw) return '';
      const [year, month, day] = String(raw).slice(0, 10).split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    },
    skilledCareNeeds() {
      const needs = this.metadata.ai_skilled_care_needs;
      if (!Array.isArray(needs)) return [];
      return needs.map((need) => SKILLED_CARE_LABELS[need] || need);
    },
    pillars() {
      const assessment = this.metadata.ai_eligibility_assessment;
      if (!assessment) return [];
      return PILLARS.filter((pillar) => assessment[pillar.key]).map((pillar) => {
        const raw = assessment[pillar.key];
        return {
          key: pillar.key,
          label: pillar.label,
          value: String(raw).replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase()),
          className: PILLAR_CLASSES[raw] || 'text-gray-700',
        };
      });
    },
    reason() {
      return this.metadata.ai_recommendation_reason || '';
    },
    missingData() {
      const items = this.metadata.ai_missing_or_uncertain_data;
      return Array.isArray(items) ? items.filter(Boolean) : [];
    },
  },
};
</script>
